/**
 * Kaysou Assist → Atendimento de Mesas (tablet de sala).
 *
 * Mostra em grade todos os chamados ativos do restaurante. Quatro mesas
 * a chamar = quatro cartões visíveis, cada um com o seu ACEPTAR/ATENDIDO.
 * Canal de tempo real próprio, separado do KDS.
 */
import { useEffect, useState } from "react";
import { useKaysouAssist } from "@/hooks/useKaysouAssist";
import { visibleRequests } from "@/utils/kaysouAssistState";
import { AssistRequestCard } from "@/components/kaysou/AssistRequestCard";
import { KaysouAssistInstallButton } from "@/components/kaysou/KaysouAssistInstallButton";
import { useKaysouAssistPWA } from "@/hooks/useKaysouAssistPWA";

export default function KaysouAssistPage() {
  useKaysouAssistPWA();
  const {
    requests, loading, wsConnected, soundOn, actionError,
    enableSound, refresh, acceptRequest, completeRequest,
  } = useKaysouAssist();
  const [nowMs, setNowMs] = useState(Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const visible = visibleRequests(requests, nowMs);

  return (
    <div className="min-h-screen bg-gray-950 text-white" data-testid="kaysou-assist-page">
      <header className="sticky top-0 z-10 bg-gray-950/95 backdrop-blur border-b border-gray-800 px-5 py-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-black tracking-wide">KAYSOU ASSIST</h1>
          <p className="text-sm text-gray-400">Atención de mesas · {visible.length} activa(s)</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`text-xs font-semibold px-2.5 py-1 rounded-full ${wsConnected ? "bg-emerald-900 text-emerald-300" : "bg-amber-900 text-amber-300"}`}
            data-testid="kaysou-assist-connection"
          >
            {wsConnected ? "En vivo" : "Reconectando"}
          </span>
          {!soundOn && (
            <button
              type="button"
              onClick={enableSound}
              className="px-3 h-9 rounded-lg bg-white text-gray-900 text-sm font-bold"
              data-testid="kaysou-assist-enable-sound"
            >
              Activar sonido
            </button>
          )}
          <KaysouAssistInstallButton />
          <button
            type="button"
            onClick={refresh}
            className="px-3 h-9 rounded-lg border border-gray-700 text-sm font-semibold"
            data-testid="kaysou-assist-refresh"
          >
            Actualizar
          </button>
        </div>
      </header>

      {actionError && (
        <p className="mx-5 mt-4 rounded-xl bg-amber-900/40 border border-amber-700 px-4 py-3 text-sm text-amber-200"
           data-testid="kaysou-assist-error">
          {actionError}
        </p>
      )}

      <main className="p-5">
        {loading && <p className="text-gray-400">Cargando llamadas…</p>}
        {!loading && visible.length === 0 && (
          <div className="mt-20 text-center text-gray-500" data-testid="kaysou-assist-empty">
            <p className="text-lg font-semibold">Sin llamadas activas</p>
            <p className="text-sm mt-1">Las mesas aparecerán aquí en cuanto pidan atención.</p>
          </div>
        )}
        {visible.length > 0 && (
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3" data-testid="kaysou-assist-grid">
            {visible.map((request) => (
              <AssistRequestCard
                key={request.id}
                request={request}
                nowMs={nowMs}
                onAccept={acceptRequest}
                onComplete={completeRequest}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
